import type { Plan, PlanComparisonRow, PlanFaq } from "@/types/plans";

export type { Plan, PlanComparisonRow, PlanFaq };

export const plans: Plan[] = [
  {
    id: "plan-free",
    slug: "free",
    name: "Free",
    tagline: "Get discovered with a basic listing, free forever.",
    priceYearly: 0,
    duration: "For Ever",
    icon: "ri-store-3-line",
    popular: false,
    highlights: [
      "1 business listing",
      "List in 1 Category",
      "Up to 3 photos",
      "Phone & WhatsApp button",
      "Appears in search & category pages",
      "Basic business details & map",
    ],
    ctaLabel: "Start free listing",
  },
  {
    id: "plan-business",
    slug: "business",
    name: "Business",
    tagline: "For growing businesses that want a steady flow of leads.",
    priceYearly: 599,
    duration: "12 months",
    icon: "ri-rocket-2-line",
    popular: true,
    highlights: [
      "1 verified listing",
      "List in One Main Category and 4 Sub Categories",
      "1 featured image + 7 photos gallery",
      "Website & social links",
      "Lead dashboard & enquiry alerts",
      "Enhanced SEO metadata",
      "Verified badge on your profile",
    ],
    ctaLabel: "Choose Business",
  },
  {
    id: "plan-premium",
    slug: "premium",
    name: "Premium",
    tagline: "Maximum visibility, priority ranking and premium branding.",
    priceYearly: 1599,
    duration: "12 months",
    icon: "ri-vip-crown-2-line",
    popular: false,
    highlights: [
      "1 verified listing",
      "List in 3 Main Categories and 6 Sub Categories",
      "1 featured image + 9 photos gallery",
      "Add a video tour (YouTube / Vimeo)",
      "Covers all features from Business Package",
      "Featured listing eligibility",
      "Priority ranking in relevant results",
    ],
    ctaLabel: "Choose Premium",
  },
  {
    id: "plan-sponsored",
    slug: "sponsored-listing",
    name: "Sponsored Listing",
    tagline: "Maximum reach — Premium visibility plus top placement across the coast.",
    priceYearly: 0,
    duration: "30 days",
    icon: "ri-megaphone-line",
    popular: false,
    highlights: [
      "Everything in the Premium plan included",
      "3 main categories + 6 sub categories",
      "Video tour enabled",
      "Served in the homepage Sponsored block",
      "Served on its district & taluk category pages",
      "Pinned to the top of its selected category pages",
      "Short-term boost — renewable every 30 days",
    ],
    ctaLabel: "Choose Sponsored",
  },
  {
    id: "plan-featured",
    slug: "featured-listing",
    name: "Featured Listing",
    tagline: "Business visibility plus a featured spot in front of local customers.",
    priceYearly: 0,
    duration: "30 days",
    icon: "ri-star-smile-line",
    popular: false,
    highlights: [
      "Everything in the Business plan included",
      "1 main category + 4 sub categories",
      "Served in the homepage Featured block",
      "Shown right after sponsored listings on its category pages",
      "Short-term boost — renewable every 30 days",
    ],
    ctaLabel: "Choose Featured",
  },
];

export const planComparison: PlanComparisonRow[] = [
  { id: "row-01", feature: "Business listings", free: "1", business: "1", premium: "1" },
  { id: "row-02", feature: "Listings per category", free: "1", business: "1 main + 4 sub", premium: "3 main + 6 sub" },
  { id: "row-02b", feature: "Photos per listing", free: "3", business: "8", premium: "10" },
  { id: "row-03", feature: "Video showcase", free: "—", business: "—", premium: "1 video" },
  { id: "row-04", feature: "Website link", free: "—", business: "Included", premium: "Included" },
  { id: "row-05", feature: "Phone & WhatsApp button", free: "Included", business: "Included", premium: "Included" },
  { id: "row-06", feature: "Lead dashboard", free: "—", business: "Included", premium: "Included" },
  { id: "row-07", feature: "Enquiry alerts", free: "—", business: "Email", premium: "Email + WhatsApp" },
  { id: "row-08", feature: "SEO metadata", free: "Basic", business: "Enhanced", premium: "Advanced" },
  { id: "row-09", feature: "Verified badge", free: "—", business: "Included", premium: "Included" },
  { id: "row-10", feature: "Featured eligibility", free: "—", business: "—", premium: "Included" },
  { id: "row-11", feature: "Support", free: "Community", business: "Priority email", premium: "Priority email" },
];

export const planFaqs: PlanFaq[] = [
  {
    id: "faq-01",
    question: "Is the free listing really free?",
    answer:
      "Yes. A basic listing with your business name, category, location and phone number is free forever (conditions apply). You only pay if you upgrade to a Business or Premium plan for extra visibility and lead tools.",
  },
  {
    id: "faq-02",
    question: "How is a paid plan billed?",
    answer:
      "Business and Premium plans are billed once a year, valid for a full 12 months from the day your payment is confirmed. There are no recurring monthly charges and no auto-renewal unless you choose it.",
  },
  {
    id: "faq-03",
    question: "Can I upgrade or downgrade my plan later?",
    answer:
      "Absolutely. You can move between Free, Business and Premium at any time from your business dashboard. Upgrades take effect immediately and any unused value is adjusted against your next billing cycle.",
  },
  {
    id: "faq-04",
    question: "What payment methods do you accept?",
    answer:
      "We support Indian payment methods including UPI, debit and credit cards, net banking and wallet payments through our secure payment gateway. All payments are protected with industry-standard encryption.",
  },
  {
    id: "faq-05",
    question: "What happens when my paid plan expires?",
    answer:
      "Your business data is never deleted. When a paid plan expires, the listing automatically reverts to the Free plan, so your profile, photos and reviews stay live while you decide whether to renew.",
  },
  {
    id: "faq-06",
    question: "Do you offer multi-location or franchise plans?",
    answer:
      "Yes. Chains, franchises and businesses with several branches across South Canara, Udupi and North Canara can get a custom plan with multiple listings under one dashboard. Contact our team for a tailored quote.",
  },
];